import React, { SetStateAction } from "react";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  SelectChangeEvent,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import variables from "../../../assets/styles/variables.module.scss";
import { DataBase } from "../../../Shared-ui/Types/common";
import { NotSelected } from "../../../Constant/Setting";

interface MyComponentProps {
  showTables: boolean;
  handleCancel: any;
  connectDatabase: any;
  dataBase: DataBase;
  setDataBase: React.Dispatch<SetStateAction<DataBase>>;
  addDatabase: any;
  isEditing: boolean
}

const DBForm: React.FC<MyComponentProps> = (props) => {
  const [databaseTypes] = React.useState([
    { value: "postgres", name: "PostgreSQL" },
    { value: "mysql", name: "MSSQL Server" },
    { value: "sqlite", name: "SQLite" },
  ]);
  const sqlLite = "sqlite";

  // Error state for form fields
  const [errors, setErrors] = React.useState({
    connectionName: "",
    databaseType: "",
    databaseName: "",
    server: "",
    user: "",
    password: "",
    port: "",
  });

  // Handle field changes and reset errors on input
  const handleChange = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = event.target as HTMLInputElement & {
      name?: string;
    }; // Ensure name is defined

    if (!name) {
      console.error("Name attribute is missing in the input/select element.");
      return;
    }

    props.setDataBase((prevVal) => ({ ...prevVal, [name]: value }));
  };

  // Validation function
  const validateForm = () => {
    let valid = true;
    let newErrors = { ...errors };

    // Common validations
    if (!props.dataBase.connectionName) {
      newErrors.connectionName = "Connection Name is required";
      valid = false;
    }

    if (
      !props.dataBase.databaseType ||
      props.dataBase.databaseType === NotSelected
    ) {
      newErrors.databaseType = "Please select a Database Type";
      valid = false;
    }

    if (!props.dataBase.databaseName) {
      newErrors.databaseName = "Database Name is required";
      valid = false;
    }

    // Specific validations if not SQLite
    if (props.dataBase.databaseType !== sqlLite) {
      if (!props.dataBase.server) {
        newErrors.server = "Server is required";
        valid = false;
      }
      if (!props.dataBase.user) {
        newErrors.user = "User ID is required";
        valid = false;
      }
      if (!props.dataBase.password) {
        newErrors.password = "Password is required";
        valid = false;
      }
      if (!props.dataBase.port) {
        newErrors.port = "Port is required";
        valid = false;
      }
    }

    setErrors(newErrors);
    return valid;
  };

  // Handle connect button click
  const handleConnect = () => {
    if (validateForm()) {
      props.connectDatabase(); // Proceed if validation passes
    }
  };

  const handleTableSelection = (tableName: string) => {
    if (!props.dataBase?.selectedTables) return; // Check if selectedTables exists

    const updatedTableList = props.dataBase.selectedTables.map((item) => {
      const key = Object.keys(item)[0];
      if (key === tableName) {
        return { [key]: !item[key] };
      }
      return item;
    });

    props.setDataBase((prevState: DataBase) => ({
      ...prevState,
      selectedTables: updatedTableList,
    }));
  };

  return (
    <Box
      sx={{
        "&::before": {
          content: '""',
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 7,
        },
      }}
    >
      <Box
        sx={{
          width: "500px",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          pt: "10px",
          position: "fixed",
          right: "0",
          top: "0",
          height: "100vh",
          background: variables.whiteColor,
          padding: "20px 30px",
          zIndex: 8,
        }}
      >
        <Box sx={{ position: "relative", height: "100%" }}>
          {!props.showTables ? (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Add new Database
                </Typography>
                <IconButton
                  aria-label="close"
                  onClick={() => props.handleCancel()}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  mt: 2,
                  pt: "5px",
                  height: "calc(100vh - 160px)",
                  overflow: "auto",
                }}
              >
                {/* Connection Name */}
                <FormControl fullWidth variant="outlined" required>
                  <InputLabel
                    htmlFor="connectionName"
                    shrink
                    sx={{ background: "#fff", px: 0.5 }}
                  >
                    Connection Name
                  </InputLabel>
                  <TextField
                    disabled={props.isEditing}
                    id="connectionName"
                    name="connectionName"
                    value={props.dataBase.connectionName}
                    placeholder="Enter the connection name"
                    onChange={handleChange}
                    sx={{ background: "white" }}
                    error={!!errors.connectionName}
                    helperText={errors.connectionName}
                  />
                </FormControl>

                {/* Database Type */}
                <FormControl fullWidth required error={!!errors.databaseType}>
                  <InputLabel
                    id="databaseType-label"
                    shrink
                    sx={{ background: "#fff", px: 0.5 }}
                  >
                    Database Type
                  </InputLabel>
                  <Select
                    disabled={props.isEditing}
                    labelId="databaseType-label"
                    id="databaseType"
                    name="databaseType"
                    value={props.dataBase.databaseType}
                    label="Database Type"
                    onChange={handleChange}
                  >
                    <MenuItem value={NotSelected}>Select Database</MenuItem>
                    {databaseTypes.map((item, index) => (
                      <MenuItem value={item.value} key={index}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.databaseType}</FormHelperText>
                </FormControl>

                {/* Database Name */}
                <FormControl fullWidth variant="outlined" required>
                  <InputLabel
                    htmlFor="databaseName"
                    shrink
                    sx={{ background: "#fff", px: 0.5 }}
                  >
                    Database Name
                  </InputLabel>
                  <TextField
                    id="databaseName"
                    name="databaseName"
                    disabled={props.isEditing}
                    value={props.dataBase.databaseName}
                    placeholder="Enter the Database Name"
                    onChange={handleChange}
                    sx={{ background: "white" }}
                    error={!!errors.databaseName}
                    helperText={errors.databaseName}
                  />
                </FormControl>

                {/* Additional Fields if NOT SQLite */}
                {props.dataBase.databaseType !== sqlLite && (
                  <>
                    <FormControl fullWidth variant="outlined" required>
                      <InputLabel
                        htmlFor="server"
                        shrink
                        sx={{ background: "#fff", px: 0.5 }}
                      >
                        Server
                      </InputLabel>
                      <TextField
                        id="server"
                        name="server"
                        value={props.dataBase.server}
                        placeholder="Enter the Server"
                        onChange={handleChange}
                        sx={{ background: "white" }}
                        error={!!errors.server}
                        helperText={errors.server}
                      />
                    </FormControl>

                    <FormControl fullWidth variant="outlined" required>
                      <InputLabel
                        htmlFor="user"
                        shrink
                        sx={{ background: "#fff", px: 0.5 }}
                      >
                        User ID
                      </InputLabel>
                      <TextField
                        id="user"
                        name="user"
                        value={props.dataBase.user}
                        placeholder="Enter the User ID"
                        onChange={handleChange}
                        sx={{ background: "white" }}
                        error={!!errors.user}
                        helperText={errors.user}
                      />
                    </FormControl>

                    <FormControl fullWidth variant="outlined" required>
                      <InputLabel
                        htmlFor="password"
                        shrink
                        sx={{ background: "#fff", px: 0.5 }}
                      >
                        Password
                      </InputLabel>
                      <TextField
                        id="password"
                        name="password"
                        type="password"
                        value={props.dataBase.password}
                        placeholder="Enter the Password"
                        onChange={handleChange}
                        sx={{ background: "white" }}
                        error={!!errors.password}
                        helperText={errors.password}
                      />
                    </FormControl>

                    <FormControl fullWidth variant="outlined" required>
                      <InputLabel
                        htmlFor="port"
                        shrink
                        sx={{ background: "#fff", px: 0.5 }}
                      >
                        Port
                      </InputLabel>
                      <TextField
                        id="port"
                        name="port"
                        value={props.dataBase.port}
                        placeholder="Enter the Port"
                        onChange={handleChange}
                        sx={{ background: "white" }}
                        error={!!errors.port}
                        helperText={errors.port}
                      />
                    </FormControl>
                  </>
                )}
              </Box>
            </Box>
          ) : (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Add new Tables
                </Typography>
                <IconButton
                  aria-label="close"
                  onClick={() => props.handleCancel()}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              <Box
                sx={{
                  height: "calc(100vh - 160px)",
                  overflow: "auto",
                }}
              >
                <FormGroup>
                  {props.dataBase?.selectedTables?.map((table, index) => (
                    <React.Fragment key={index}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={table[Object.keys(table)[0]]}
                            onChange={() =>
                              handleTableSelection(Object.keys(table)[0])
                            }
                          />
                        }
                        label={Object.keys(table)[0]}
                      />
                      <Divider />
                    </React.Fragment>
                  ))}
                </FormGroup>
              </Box>
            </Box>
          )}

          <Box
            sx={{
              width: "100%",
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
              position: "absolute",
              bottom: 0,
            }}
          >
            <Button
              fullWidth
              variant="outlined"
              size="large"
              sx={{ textTransform: "capitalize" }}
              onClick={props.handleCancel}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              size="large"
              sx={{ textTransform: "capitalize" }}
              onClick={!props.showTables ? handleConnect : props.addDatabase}
            >
              {!props.showTables ? "Connect" : "Submit"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DBForm;
